import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Theme,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import NumericTextField from "../../../../../views/components/common/pop-up/NumericTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import InventoryAPI from "../../../api/InventoryAPI";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import {
  InventoryPackSummary,
  InventoryVariant,
} from "../../../../../models/inventory/InventoryAdmin";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

type SkuTab = "items" | "packs";

type SelectedItemLine = {
  type: "ITEM";
  uniqueId: string;
  clientSku: string;
  productName?: string;
  free: number;
  quantity: number | string;
};

type SelectedPackLine = {
  type: "PACK";
  uniqueId: string;
  packId: number;
  label: string;
  buildableNow: number;
  quantity: number | string;
};

type SelectedLine = SelectedItemLine | SelectedPackLine;

interface AddShipmentLinesSectionProps {
  open: boolean;
  shipmentId: number;
  inventoryId: number | null | undefined;
  shipmentsAPI: ShipmentsAPI;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const hasUsableInventoryId = (inventoryId: number | null | undefined) => {
  try {
    const parsed = Number(inventoryId);
    return Number.isFinite(parsed) && parsed > 0;
  } catch {
    return false;
  }
};

const inventoryAPI = new InventoryAPI();

const generateUniqueId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const AddShipmentLinesSection: React.FC<AddShipmentLinesSectionProps> = ({
  open,
  shipmentId,
  inventoryId,
  shipmentsAPI,
  onClose,
  onSaved,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  const [selectedLines, setSelectedLines] = useState<SelectedLine[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [skuTab, setSkuTab] = useState<SkuTab>("items");
  const [variants, setVariants] = useState<InventoryVariant[]>([]);
  const [packs, setPacks] = useState<InventoryPackSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [saving, setSaving] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 400);
  const lastFetchedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      setSkuTab("items");
      setSelectedLines([]);
      setErrorMessage("");
      setInfoMessage("");
      setListError("");
      setVariants([]);
      setPacks([]);
      setSaving(false);
      lastFetchedKey.current = null;
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!hasUsableInventoryId(inventoryId)) {
      setLoadingList(false);
      setVariants([]);
      setPacks([]);
      setListError(
        "This shipment has no inventory assigned, so items and packs cannot be loaded.",
      );
      lastFetchedKey.current = null;
      return;
    }

    const cacheKey = `${inventoryId}-${skuTab}-${debouncedSearchText.trim()}`;
    if (lastFetchedKey.current === cacheKey) return;
    lastFetchedKey.current = cacheKey;

    const fetchList = async () => {
      setLoadingList(true);
      setListError("");
      try {
        const search = debouncedSearchText.trim() || undefined;
        const resolvedInventoryId = Number(inventoryId);
        if (skuTab === "items") {
          const response = await inventoryAPI.getVariants({
            inventoryId: resolvedInventoryId,
            search,
          });
          setVariants(response?.variants ?? []);
        } else {
          const response = await inventoryAPI.listPacks({
            inventoryId: resolvedInventoryId,
            search,
          });
          setPacks(response?.packs ?? []);
        }
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        setListError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load SKUs",
        );
        if (skuTab === "items") setVariants([]);
        else setPacks([]);
      } finally {
        setLoadingList(false);
      }
    };

    void fetchList();
  }, [open, inventoryId, debouncedSearchText, skuTab]);

  if (!open) return null;

  const handleAddItem = (variant: InventoryVariant) => {
    const alreadyAdded = selectedLines.some(
      (line) => line.type === "ITEM" && line.clientSku === variant.clientSku,
    );
    if (alreadyAdded) {
      setErrorMessage("");
      setInfoMessage(
        `"${variant.clientSku}" is already selected. Update the quantity below instead.`,
      );
      return;
    }

    setSelectedLines((prev) => [
      ...prev,
      {
        type: "ITEM",
        uniqueId: generateUniqueId(),
        clientSku: variant.clientSku,
        productName: variant.productName,
        free: variant.free,
        quantity: 1,
      },
    ]);
    setErrorMessage("");
    setInfoMessage("");
  };

  const handleAddPack = (pack: InventoryPackSummary) => {
    const alreadyAdded = selectedLines.some(
      (line) => line.type === "PACK" && line.packId === pack.packId,
    );
    if (alreadyAdded) {
      setErrorMessage("");
      setInfoMessage(
        `"${pack.label}" is already selected. Update the quantity below instead.`,
      );
      return;
    }

    setSelectedLines((prev) => [
      ...prev,
      {
        type: "PACK",
        uniqueId: generateUniqueId(),
        packId: pack.packId,
        label: pack.label,
        buildableNow: pack.buildableNow,
        quantity: 1,
      },
    ]);
    setErrorMessage("");
    setInfoMessage("");
  };

  const handleRemoveLine = (uniqueId: string) => {
    setSelectedLines((prev) =>
      prev.filter((line) => line.uniqueId !== uniqueId),
    );
  };

  const handleQuantityChange = (uniqueId: string, value: number | string) => {
    setSelectedLines((prev) =>
      prev.map((line) =>
        line.uniqueId === uniqueId ? { ...line, quantity: value } : line,
      ),
    );
  };

  const validateLines = () => {
    setInfoMessage("");
    if (selectedLines.length === 0) {
      setErrorMessage("Please add at least one item or pack.");
      return false;
    }

    for (const line of selectedLines) {
      const qty = Number(line.quantity);
      if (!line.quantity || !Number.isFinite(qty) || qty <= 0) {
        setErrorMessage("Please ensure all lines have a valid quantity.");
        return false;
      }
    }

    setErrorMessage("");
    return true;
  };

  const handleSave = async () => {
    if (!validateLines()) return;

    setSaving(true);
    setErrorMessage("");
    try {
      for (const line of selectedLines) {
        if (line.type === "PACK") {
          await shipmentsAPI.addShipmentPackLine(shipmentId, {
            packId: line.packId,
            qty: Number(line.quantity),
          });
        } else {
          await shipmentsAPI.addShipmentItemLine(shipmentId, {
            clientSku: line.clientSku,
            qty: Number(line.quantity),
          });
        }
      }

      await onSaved();
      onClose();
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add items to shipment.",
      );
    } finally {
      setSaving(false);
    }
  };

  const missingInventory = !hasUsableInventoryId(inventoryId);
  const listEmpty =
    !loadingList &&
    !listError &&
    !missingInventory &&
    (skuTab === "items" ? variants.length === 0 : packs.length === 0);

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.surface,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1.25,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surfaceMuted,
        }}
      >
        <CustomTypography
          variant="body2"
          fontWeight={600}
          sx={{ color: colors.title }}
        >
          Add items / packs
        </CustomTypography>
        <IconButton
          size="small"
          aria-label="Close add section"
          onClick={onClose}
          disabled={saving}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <CustomTextField
          placeholder="Search by SKU or product name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value || "")}
          width="100%"
          customStyles={{ marginBottom: 4 }}
          disabled={missingInventory || saving}
        />

        <Tabs
          value={skuTab}
          onChange={(_, v: SkuTab) => setSkuTab(v)}
          sx={{
            minHeight: 34,
            mb: 1,
            "& .MuiTab-root": {
              minHeight: 34,
              py: 0.25,
              fontSize: "0.8rem",
              textTransform: "none",
            },
            "& .MuiTabs-indicator": {
              height: 2,
              borderRadius: "2px 2px 0 0",
            },
          }}
        >
          <Tab label="Items" value="items" />
          <Tab label="Packs" value="packs" />
        </Tabs>

        <Box
          sx={{
            maxHeight: 220,
            overflowY: "auto",
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            mb: 1.5,
          }}
        >
          {loadingList && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {listError && (
            <CustomTypography
              variant="body2"
              sx={{ color: theme.palette.error.main, p: 1.5 }}
            >
              {listError}
            </CustomTypography>
          )}

          {listEmpty && (
            <CustomTypography
              variant="body2"
              sx={{ color: colors.subtitle, p: 1.5 }}
            >
              No {skuTab === "items" ? "items" : "packs"} found
              {debouncedSearchText.trim()
                ? ` for “${debouncedSearchText.trim()}”`
                : " in this inventory"}
              .
            </CustomTypography>
          )}

          {skuTab === "items" &&
            !loadingList &&
            variants.map((variant) => (
              <Box
                key={variant.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  borderBottom: `1px solid ${colors.border}`,
                  "&:last-child": { borderBottom: 0 },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {variant.clientSku}
                  </CustomTypography>
                  {variant.productName && (
                    <CustomTypography
                      variant="body2"
                      sx={{
                        fontSize: "0.72rem",
                        color: colors.subtitle,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {variant.productName}
                    </CustomTypography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CustomTypography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: colors.subtitle }}
                  >
                    {variant.free}
                  </CustomTypography>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={saving}
                    onClick={() => handleAddItem(variant)}
                  >
                    <AddIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              </Box>
            ))}

          {skuTab === "packs" &&
            !loadingList &&
            packs.map((pack) => (
              <Box
                key={pack.packId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  borderBottom: `1px solid ${colors.border}`,
                  "&:last-child": { borderBottom: 0 },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {pack.label}
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    sx={{ fontSize: "0.72rem", color: colors.subtitle }}
                  >
                    Pack #{pack.packId}
                  </CustomTypography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CustomTypography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: colors.subtitle }}
                  >
                    {pack.buildableNow}
                  </CustomTypography>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={saving}
                    onClick={() => handleAddPack(pack)}
                  >
                    <AddIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
        </Box>

        <CustomTypography
          variant="body2"
          fontWeight={600}
          sx={{ color: colors.title, mb: 1, fontSize: "0.8rem" }}
        >
          Selected ({selectedLines.length})
        </CustomTypography>

        {selectedLines.length === 0 ? (
          <CustomTypography
            variant="body2"
            sx={{ color: colors.subtitle, mb: 1.5, fontSize: "0.78rem" }}
          >
            Tap + on an item or pack to add it here.
          </CustomTypography>
        ) : (
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}
          >
            {selectedLines.map((line) => (
              <Box
                key={line.uniqueId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.surfaceMuted,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {line.type === "ITEM" ? line.clientSku : line.label}
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    sx={{ fontSize: "0.72rem", color: colors.subtitle }}
                  >
                    {line.type === "ITEM"
                      ? line.productName || `Free: ${line.free}`
                      : `Pack #${line.packId} · Buildable: ${line.buildableNow}`}
                  </CustomTypography>
                </Box>
                <NumericTextField
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "12px",
                      width: "52px",
                      textAlign: "center",
                    },
                  }}
                  type="number"
                  placeholder="Qty"
                  size="small"
                  value={line.quantity}
                  disabled={saving}
                  onChange={(e) =>
                    handleQuantityChange(
                      line.uniqueId,
                      e.target.value === "" ? "" : parseInt(e.target.value),
                    )
                  }
                />
                <IconButton
                  size="small"
                  disabled={saving}
                  onClick={() => handleRemoveLine(line.uniqueId)}
                >
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {(errorMessage || infoMessage) && (
          <CustomTypography
            variant="body2"
            sx={{
              mb: 1.25,
              color: errorMessage
                ? theme.palette.error.main
                : theme.palette.warning.main,
            }}
          >
            {errorMessage || infoMessage}
          </CustomTypography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <AppButton
            label="Close"
            variant="secondary"
            disabled={saving}
            onClick={onClose}
            customStyles={{ minHeight: 34, px: 1.5 }}
            fontSize="0.75rem"
          />
          <AppButton
            label={saving ? "Saving…" : "Save items"}
            variant="primary"
            disabled={
              saving || selectedLines.length === 0 || missingInventory
            }
            onClick={handleSave}
            customStyles={{ minHeight: 34, px: 1.5 }}
            fontSize="0.75rem"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddShipmentLinesSection;
